import { useState } from "react";
import type { CatalogProduct } from "@/mocks/catalogData";

interface CatalogProductCardProps {
  product: CatalogProduct;
}

export default function CatalogProductCard({ product }: CatalogProductCardProps) {
  const [liked, setLiked] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  return (
    <>
      <article className="group bg-[#F7F5F2] cursor-pointer" onClick={() => setShowDetail(true)}>
        {/* Image */}
        <div className="relative aspect-[3/4] overflow-hidden bg-[#EFEDE9]">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-700"
            loading="lazy"
          />
          {product.badge && (
            <div
              className="absolute top-4 left-4 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] bg-[#B8422E] text-white"
              style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
            >
              {product.badge}
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-[#1A1C1E]/0 group-hover:bg-[#1A1C1E]/10 transition-colors duration-500" />
          {/* CTA on hover */}
          <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
            <button
              className="w-full py-2.5 bg-[#1A1C1E] text-white text-[11px] font-semibold tracking-[0.1em] hover:bg-[#B8422E] transition-colors duration-300 whitespace-nowrap"
              style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
              onClick={(e) => { e.stopPropagation(); setShowDetail(true); }}
            >
              View in Showroom
            </button>
          </div>
          {/* Wishlist */}
          <button
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white transition-colors duration-200 opacity-0 group-hover:opacity-100"
            style={{ borderRadius: "4px" }}
            onClick={(e) => { e.stopPropagation(); setLiked(!liked); }}
            aria-label="Save to wishlist"
          >
            <i className={`${liked ? "ri-heart-fill text-[#B8422E]" : "ri-heart-line text-[#1A1C1E]"} text-sm`} />
          </button>
        </div>

        {/* Info */}
        <div className="p-5 border-t border-[#1A1C1E]/[0.06]">
          <p
            className="text-[10px] uppercase tracking-[0.14em] text-[#6C7278] mb-1"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {product.subcategory}
          </p>
          <h3
            className="text-sm font-semibold text-[#1A1C1E] truncate mb-1"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            {product.name}
          </h3>
          <p
            className="text-[11px] text-[#6C7278] mb-3"
            style={{ fontFamily: "'Public Sans', sans-serif" }}
          >
            {product.material}
          </p>
          <div className="flex items-center justify-between">
            <p
              className="text-base font-bold text-[#B8422E]"
              style={{ fontFamily: "'Public Sans', sans-serif" }}
            >
              {product.priceDisplay}
            </p>
            <span
              className="text-[10px] text-[#6C7278]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {product.lead}
            </span>
          </div>
        </div>
      </article>

      {/* Detail modal — full-screen editorial */}
      {showDetail && (
        <div
          className="fixed inset-0 z-50 bg-[#0d0d0d] flex flex-col md:flex-row overflow-hidden"
          style={{ animation: "fadeIn 0.25s ease" }}
        >
          {/* Close */}
          <button
            className="absolute top-5 right-5 z-20 w-10 h-10 flex items-center justify-center border border-white/15 text-[#6C7278] hover:border-white/40 hover:text-white transition-all duration-200 cursor-pointer"
            style={{ borderRadius: "4px" }}
            onClick={() => setShowDetail(false)}
          >
            <i className="ri-close-line text-lg" />
          </button>

          {/* Left — large image, fills half the screen */}
          <div className="w-full md:w-[55%] h-[50vh] md:h-screen flex-shrink-0 relative overflow-hidden bg-[#111213]">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover object-top"
            />
            {/* Subtle dark gradient at bottom */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0d0d0d]/60 to-transparent" />
            {/* Badge */}
            {product.badge && (
              <div
                className="absolute top-6 left-6 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] bg-[#B8422E] text-white"
                style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
              >
                {product.badge}
              </div>
            )}
            {/* Bottom-left label */}
            <div className="absolute bottom-6 left-6">
              <p
                className="text-[10px] uppercase tracking-[0.22em] text-white/40"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {product.category}
              </p>
            </div>
          </div>

          {/* Right — details panel */}
          <div className="flex-1 flex flex-col justify-between overflow-y-auto px-8 md:px-14 py-10 md:py-16">
            <div>
              {/* Breadcrumb */}
              <p
                className="text-[10px] uppercase tracking-[0.28em] text-[#B8422E] mb-8"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                / {product.subcategory}
              </p>

              {/* Product name — huge */}
              <h2
                className="text-4xl md:text-5xl lg:text-[60px] font-extrabold text-white leading-[1.02] tracking-[-0.03em] mb-6"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {product.name}
              </h2>

              {/* Price */}
              <p
                className="text-3xl md:text-4xl font-extrabold text-[#B8422E] tracking-[-0.02em] mb-10"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                {product.priceDisplay}
              </p>

              {/* Divider */}
              <div className="h-px bg-white/[0.06] mb-8" />

              {/* Specs */}
              <div className="flex flex-col gap-5">
                {[
                  { label: "Material", value: product.material },
                  { label: "Dimensions", value: product.dimensions },
                  { label: "Lead Time", value: product.lead },
                ].map((spec) => (
                  <div key={spec.label} className="flex items-start gap-6">
                    <span
                      className="text-[10px] uppercase tracking-[0.18em] text-[#6C7278] w-24 flex-shrink-0 pt-0.5"
                      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                      {spec.label}
                    </span>
                    <span
                      className="text-sm text-white/70 leading-relaxed"
                      style={{ fontFamily: "'Public Sans', sans-serif" }}
                    >
                      {spec.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="h-px bg-white/[0.06] mt-8 mb-6" />

              {/* Showroom note */}
              <p
                className="text-xs text-[#6C7278] leading-relaxed"
                style={{ fontFamily: "'Public Sans', sans-serif" }}
              >
                <i className="ri-map-pin-line mr-1.5 text-[#B8422E]" />
                On display at our West Loop showroom — 820 W. Randolph St, Chicago.
              </p>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col gap-3">
              <a
                href="/showroom"
                className="w-full py-4 bg-white text-[#1A1C1E] text-[12px] font-bold tracking-[0.14em] uppercase hover:bg-[#B8422E] hover:text-white transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
              >
                <i className="ri-store-2-line" />
                Visit the Showroom
              </a>
              <button
                onClick={() => setLiked(!liked)}
                className="w-full py-4 border border-white/15 text-white text-[12px] font-semibold tracking-[0.14em] uppercase hover:border-white/40 transition-colors duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                style={{ fontFamily: "'Space Grotesk', sans-serif", borderRadius: "4px" }}
              >
                <i className={liked ? "ri-heart-fill text-[#B8422E]" : "ri-heart-line"} />
                {liked ? "Saved" : "Save to Wishlist"}
              </button>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
